import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import DeleteIcon from '@atlaskit/icon/core/delete';
import {
	Flex,
	Inline,
	Pressable,
	type PressableProps,
	Stack,
	Text,
	xcss,
	// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
} from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import { useExtensionsState } from '../../../../controllers/use-extensions-state';
import type { Item as ItemType } from '../../../../types';
import { DraggableItemContainer, type DraggableItemContainerProps } from '../../draggable-items';
import type { RemoveItemType } from '../../types';

import { messages } from './messages';

export enum ITEM_LABEL {
	CARD = 'card',
	FRAME = 'frame',
}

const iconColors = {
	hoverActive: token('color.icon.danger'),
	default: token('color.icon.subtle'),
	disabled: token('color.text.disabled'),
};

const removeActiveStyles = xcss({
	':hover': {
		background: token('color.background.danger'),
		color: 'color.text.danger',
	},
	':focus': {
		background: token('color.background.danger'),
		color: 'color.text.danger',
	},
});

const removeButtonStyles = xcss({
	alignItems: 'center',
	borderRadius: 'border.radius.100',
	display: 'flex',
	height: '32px',
	justifyContent: 'center',
});

const dragHandleFocusedWrapperStyles = xcss({
	outline: `2px solid ${token('color.border.focused')}`,
});

const removeHoveredWrapperStyles = xcss({
	outline: `2px solid ${token('color.border.danger')}`,
});

const collapsibleStyles = xcss({
	borderRadius: 'border.radius.100',
	outline: `1px solid ${token('color.border')}`,
});

const collapsibleStylesV2 = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
});

const collapsibleTriggerStyles = xcss({
	paddingLeft: 'space.0',
	paddingRight: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	':focus': {
		borderRadius: 'border.radius.100',
	},
	width: '100%',
});

const collapsibleTriggerStylesV2 = xcss({
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	width: '100%',
});

const collapsibleContentStyles = xcss({
	width: '100%',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.050',
	paddingBottom: 'space.150',
});

const collapsibleContentStylesV2 = xcss({
	width: '100%',
	paddingLeft: 'space.050',
	paddingRight: 'space.0',
	paddingTop: 'space.050',
	paddingBottom: 'space.150',
});

export type ItemProps = {
	actionSubjectIdPostFix: string;
	analyticsSource: string;
	itemParameters: ItemType;
	isDeleteDisabled?: boolean;
	isExpanded: boolean;
	isFirstItem: boolean;
	isLastItem: boolean;
	onBlur(): void;
	onFocus(): void;
	order: number;
	removeItem: RemoveItemType;
	renderItemContent(itemParameters: ItemType, isItemDisabled: boolean): ReactNode;
	setExpandedItem(cardId: string): void;
	setDraggedItem(): void;
	shouldUseCardV2UI?: boolean;
	itemLabel?: ITEM_LABEL;
} & Pick<DraggableItemContainerProps, 'swapItems'>;

export const Item = ({
	actionSubjectIdPostFix,
	analyticsSource,
	itemParameters,
	isDeleteDisabled,
	isExpanded,
	isFirstItem,
	isLastItem,
	onBlur,
	onFocus,
	order,
	removeItem,
	setDraggedItem,
	setExpandedItem,
	swapItems,
	shouldUseCardV2UI = false,
	itemLabel = ITEM_LABEL.CARD,
	renderItemContent,
}: ItemProps) => {
	const { formatMessage } = useIntl();
	const itemRef = useRef<HTMLDivElement>(null);
	const [
		{ isInAutoFocusMode, selectedToRemoveCardId },
		{ setIsInAutoFocusMode, setSelectedToRemoveCardId },
	] = useExtensionsState();
	const CollapsedCardIcon = shouldUseCardV2UI ? ChevronDownIcon : ChevronRightIcon;

	const { cardId, title } = itemParameters;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isItemDisabled = useIsDisabledContext();

	const onRemove = useCallback(() => {
		removeItem(cardId);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `delete${actionSubjectIdPostFix}`,
				source: analyticsSource,
			},
		}).fire();
	}, [cardId, removeItem, createAnalyticsEvent, analyticsSource, actionSubjectIdPostFix]);

	const isSelectedToRemove = selectedToRemoveCardId === cardId;

	let removeButtonColor;
	if (isDeleteDisabled || isItemDisabled) {
		removeButtonColor = iconColors.disabled;
	} else {
		if (isSelectedToRemove) {
			removeButtonColor = iconColors.hoverActive;
		} else {
			removeButtonColor = iconColors.default;
		}
	}

	const [isDragHandleFocused, setIsDragHandleFocused] = useState<boolean>(false);

	const isRemoveDisabled = isDeleteDisabled || isItemDisabled;
	const selectItemToRemove = useCallback(() => {
		!isRemoveDisabled && setSelectedToRemoveCardId(cardId);
	}, [isRemoveDisabled, cardId, setSelectedToRemoveCardId]);
	const unselectItemToRemove = useCallback(() => {
		setSelectedToRemoveCardId(null);
	}, [setSelectedToRemoveCardId]);

	const removeItemProps = {
		isDisabled: isRemoveDisabled,
		onClick: onRemove,
		onMouseOver: selectItemToRemove,
		onFocus: selectItemToRemove,
		onMouseOut: unselectItemToRemove,
		onBlur: unselectItemToRemove,
	};

	const onCollapsibleTriggerClick: PressableProps['onClick'] = () => {
		setIsInAutoFocusMode(true);
		setExpandedItem(isExpanded ? '' : cardId);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: isExpanded ? 'collapsed' : 'expanded',
				actionSubject: 'card',
				actionSubjectId: 'cardCollapsible',
				source: analyticsSource,
			},
		}).fire();
	};

	useEffect(() => {
		if (isExpanded && itemRef.current && isInAutoFocusMode) {
			itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [isExpanded, itemRef, isInAutoFocusMode]);

	let collapseLabel = messages.collapseCardLabel,
		expandLabel = messages.expandCardLabel,
		removeLabel = messages.removeCardLabel,
		headerTitle = formatMessage(messages.cardTitle, { order });
	if (itemLabel === ITEM_LABEL.FRAME) {
		// @TODO, switch over to these messages unconditionally once
		// translations are available. The itemLabel context
		// is provided for correct string selection.
		collapseLabel = messages.collapseItemLabel;
		expandLabel = messages.expandItemLabel;
		removeLabel = messages.removeItemLabel;
		headerTitle = formatMessage(messages.itemTitle, { itemLabel, order });
	}
	headerTitle = title || headerTitle;

	return (
		<Stack
			xcss={[
				shouldUseCardV2UI ? collapsibleStylesV2 : collapsibleStyles,
				isSelectedToRemove && !isRemoveDisabled && !shouldUseCardV2UI
					? removeHoveredWrapperStyles
					: isDragHandleFocused && dragHandleFocusedWrapperStyles,
			]}
			testId="item-wrapper"
			ref={itemRef}
			grow="fill"
			alignBlock="center"
			alignInline="start"
		>
			<DraggableItemContainer
				index={order - 1}
				item={itemParameters}
				headerTitle={headerTitle}
				isDragHandleFocused={isDragHandleFocused}
				isFirstItem={isFirstItem}
				isLastItem={isLastItem}
				setDraggedItem={setDraggedItem}
				setIsDragHandleFocused={setIsDragHandleFocused}
				swapItems={swapItems}
				shouldUseCardV2UI={shouldUseCardV2UI}
				onFocus={onFocus}
				onBlur={onBlur}
			>
				<Pressable
					onClick={onCollapsibleTriggerClick}
					onBlur={onBlur}
					onFocus={onFocus}
					backgroundColor="color.background.neutral.subtle"
					xcss={[shouldUseCardV2UI ? collapsibleTriggerStylesV2 : collapsibleTriggerStyles]}
				>
					<Inline alignBlock="center" spread="space-between" space="space.050">
						<Text
							color="color.text"
							size={shouldUseCardV2UI ? 'medium' : undefined}
							weight={shouldUseCardV2UI ? 'semibold' : undefined}
							maxLines={1}
						>
							{headerTitle}
						</Text>
						{isExpanded ? (
							<ChevronUpIcon
								color={token('color.text.subtle')}
								label={formatMessage(collapseLabel)}
								size="small"
							/>
						) : (
							<CollapsedCardIcon
								color={token('color.text.subtle')}
								label={formatMessage(expandLabel)}
								size="small"
							/>
						)}
					</Inline>
				</Pressable>
			</DraggableItemContainer>
			{isExpanded && (
				<Flex
					direction="column"
					xcss={[shouldUseCardV2UI ? collapsibleContentStylesV2 : collapsibleContentStyles]}
					testId="item-content"
				>
					{renderItemContent(itemParameters, isItemDisabled)}
					<Pressable
						{...removeItemProps}
						xcss={[removeButtonStyles, !isRemoveDisabled && removeActiveStyles]}
					>
						<Flex columnGap="space.050" alignItems="center">
							<DeleteIcon
								color={removeButtonColor}
								label={formatMessage(removeLabel, { itemLabel })}
							/>
							<FormattedMessage {...removeLabel} values={{ itemLabel }} />
						</Flex>
					</Pressable>
				</Flex>
			)}
		</Stack>
	);
};
